import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

import { getVisualWorkflowExecutionV1 } from "../../lib/brandStudioApi";

const WORKFLOW_LABELS = {
  photography_direction: "Фотография",
  material_direction: "Материалы / фактуры",
  graphic_presentation_direction: "Графический язык / подача",
};

const STATUS_LABELS = {
  executable: "готово к исполнению",
  not_applicable: "не требуется",
  blocked: "заблокировано",
};

const SIGNAL_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  composition: "Композиция",
  photography: "Фотография",
  graphics: "Графика",
  packaging: "Упаковка",
  material_texture: "Материал / фактура",
  mood: "Настроение",
  logo_mark: "Логотип / знак",
  brandbook_presentation: "Подача брендбука",
  whole: "Всё целиком",
  other: "Другое",
};

const ROLE_LABELS = {
  primary_visual_reference: "основной",
  whole_style_reference: "целостный",
  supporting_reference: "поддерживающий",
};

const REJECT_LABELS = {
  pseudo_text: "псевдотекст",
  invented_logo: "выдуманный логотип",
  invented_monogram: "выдуманная монограмма",
  identity_hallucination: "выдуманная айдентика",
  mutant_geometry: "мутантная геометрия",
  implausible_object: "неправдоподобный объект",
  severe_visual_artifact: "грубый визуальный артефакт",
  wrong_workflow: "результат не по задаче",
  locked_rule_violation: "нарушено зафиксированное решение",
  visual_style_mismatch: "несоответствие визуальному стилю",
};

function ReferenceCard({ item }) {
  return (
    <div className="rounded-[12px] border border-[#E4D8C8] bg-[#FFFDF9] p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10.5px] font-medium text-[#4B392B]">
          Референс · ранг {item.rank}
        </div>
        <span className="text-[9px] text-[#8A745E]">
          {ROLE_LABELS[item.role_in_execution] || item.role_in_execution}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {(item.user_signals || []).map((signal) => (
          <span
            key={signal}
            className="rounded-full border border-[#E3D6C4] px-2 py-0.5 text-[9px] text-[#7A624D]"
          >
            {SIGNAL_LABELS[signal] || signal}
          </span>
        ))}
      </div>
      {item.identity_content_present && (
        <div className="mt-2 text-[9px] leading-4 text-[#8B5D52]">
          Есть визуальная айдентика: копировать её запрещено.
        </div>
      )}
    </div>
  );
}

export default function VisualWorkflowExecutionOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getVisualWorkflowExecutionV1()
      .then((value) => {
        if (cancelled) return;
        setResult(value);
        setStatus("ready");
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(
          requestError?.response?.data?.detail ||
            "Не удалось открыть техническое задание.",
        );
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="rounded-[24px] border border-dashed border-[#8C7359] bg-[#FFF8ED] p-5 md:p-7"
      data-testid="visual-workflow-execution-owner-panel-v1"
    >
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">
        ПРОВЕРКА ВЛАДЕЛЬЦА · WE1
      </div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">
        Технические задания для визуальных сценариев
      </h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Brand Studio превращает каждый WP1-сценарий в отдельное структурированное
        машинное задание. Провайдер, модель и внешний промпт здесь ещё не выбираются.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" />
          Собираю технические задания…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {String(error)}
        </div>
      )}

      {status === "ready" && result?.result === "not_ready" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result.reason || "Сначала должен быть готов Workflow Planner."}
        </div>
      )}

      {status === "ready" && result?.result === "blocked" && !result?.contracts?.length && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] text-[#6B443A]">
          {result.reason || "Execution Contract заблокирован."}
        </div>
      )}

      {!!result?.contracts?.length && (
        <div className="mt-6 space-y-4 border-t border-[#E2D5C3] pt-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              <Check size={12} /> 3 отдельных задания
            </span>
            <span className="text-[10.5px] text-[#8A745E]">
              вызовы провайдера: 0 · ИИ-синтез: 0 · стоимость: 0
            </span>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            {result.contracts.map((contract) => (
              <article
                key={contract.workflow_type}
                className="rounded-[16px] border border-[#DED0BC] bg-white p-4"
                data-testid={"execution-contract-" + contract.workflow_type}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.14em] text-[#927A5B]">
                      {WORKFLOW_LABELS[contract.workflow_type] || contract.workflow_type}
                    </div>
                    <p className="mt-2 text-[11px] leading-5 text-[#655141]">
                      {contract.task.system_task_definition}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#F3EBDD] px-2.5 py-1 text-[9px] text-[#6D5842]">
                    {STATUS_LABELS[contract.execution_status] || contract.execution_status}
                  </span>
                </div>

                <div className="mt-3 text-[10px] leading-5 text-[#8A745E]">
                  Референсов в задании: {contract.reference_manifest?.length || 0}
                  <br />
                  Обязательных правил: {contract.instruction_spec?.mandatory_rules?.length || 0}
                  <br />
                  Провайдер: не выбран · модель: не выбрана
                </div>

                <div className="mt-3">
                  <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                    Что результат обязан показать
                  </div>
                  <div className="mt-2 space-y-1">
                    {(contract.quality_contract?.workflow_requirements || []).slice(0, 3).map((item) => (
                      <div key={item} className="text-[10.5px] leading-4 text-[#4C392B]">
                        • {item}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                    Жёсткие запреты
                  </div>
                  <div className="mt-2 text-[10px] leading-4 text-[#6B5543]">
                    Нельзя придумывать логотип, монограмму, читаемый фирменный текст,
                    точный шрифт или фирменный продукт только из профессии пользователя.
                  </div>
                </div>

                <div className="mt-3 space-y-2">
                  {(contract.reference_manifest || []).slice(0, 4).map((item) => (
                    <ReferenceCard key={item.reference_id} item={item} />
                  ))}
                  {(contract.reference_manifest || []).length > 4 && (
                    <div className="text-[9.5px] text-[#927A5B]">
                      + ещё {contract.reference_manifest.length - 4} референсов — в деталях
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          <details
            className="rounded-[14px] border border-[#DED0BC] bg-white"
            data-testid="we1-technical-details"
          >
            <summary className="cursor-pointer px-4 py-3 text-[11.5px] font-medium text-[#4C392B]">
              Показать технические задания
            </summary>
            <div className="border-t border-[#EEE4D6] px-4 py-4">
              <div className="grid gap-4 lg:grid-cols-3">
                {result.contracts.map((contract) => (
                  <div key={contract.workflow_type}>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#927A5B]">
                      {WORKFLOW_LABELS[contract.workflow_type] || contract.workflow_type}
                    </div>
                    <div className="mt-2 text-[9.5px] leading-4 text-[#796652]">
                      Отпечаток исполнения: {contract.execution_fingerprint}
                      <br />
                      Отпечаток WP1: {contract.workflow_plan_fingerprint}
                      <br />
                      Версия инструкции: {contract.instruction_schema_version}
                      <br />
                      Провайдер: не выбран · модель: не выбрана · пакет провайдера: не создаётся
                    </div>

                    <div className="mt-3 text-[9.5px] uppercase tracking-[0.12em] text-[#927A5B]">
                      Причины автоматического отклонения
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(contract.quality_contract?.common_reject_reasons || []).map((reason) => (
                        <span
                          key={reason}
                          className="rounded-full border border-[#E3D6C4] px-2 py-1 text-[9px] text-[#7A624D]"
                        >
                          {REJECT_LABELS[reason] || reason}
                        </span>
                      ))}
                    </div>

                    <div className="mt-3 space-y-2">
                      {(contract.reference_manifest || []).map((item) => (
                        <ReferenceCard key={item.reference_id} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
